export const REQUIRED_COMPONENTS = [
  'Roof',
  'Load-bearing structure',
  'Fire protection',
  'Plumbing',
  'Electrical',
  'Waterproofing/exterior painting',
  'Windows/exterior doors',
  'Other items over $25,000',
];

export const EXTRACTION_SYSTEM_PROMPT = `You extract Florida SIRS (Structural Integrity Reserve Study) component data from engineering reports.

Return ONLY valid JSON — no markdown, no commentary.

Output must be a JSON array. Each object must include:
- name (string)
- estimatedReplacementCost (number)
- remainingUsefulLifeYears (number)
- currentReserveBalance (number)

Use these exact component names where applicable when mapping to the 8 HB 913 mandated categories:
${REQUIRED_COMPONENTS.join(', ')}

Missing numeric values should be 0.`;

function sanitizeClaudeJsonOutput(rawOutput) {
  const trimmed = (rawOutput || '').trim();
  if (!trimmed) return '[]';

  const fencedMatch = trimmed.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);
  if (fencedMatch?.[1]) return fencedMatch[1].trim();

  const firstArray = trimmed.indexOf('[');
  const lastArray = trimmed.lastIndexOf(']');
  if (firstArray !== -1 && lastArray !== -1 && lastArray > firstArray) {
    return trimmed.slice(firstArray, lastArray + 1).trim();
  }

  const firstObject = trimmed.indexOf('{');
  const lastObject = trimmed.lastIndexOf('}');
  if (firstObject !== -1 && lastObject !== -1 && lastObject > firstObject) {
    const objectJson = trimmed.slice(firstObject, lastObject + 1).trim();
    return `[${objectJson}]`;
  }

  return '[]';
}

async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      const base64 = result.includes(',') ? result.split(',')[1] : result;
      if (!base64) {
        reject(new Error('Could not convert PDF to base64.'));
        return;
      }
      resolve(base64);
    };
    reader.onerror = () => reject(new Error('Failed to read uploaded PDF.'));
    reader.readAsDataURL(file);
  });
}

export async function extractSirsComponentsFromPdf(file) {
  const pdfBase64 = await fileToBase64(file);
  const filename = file.name;

  const response = await fetch('/api/parse-sirs', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model: 'claude-sonnet-4-6',
      max_tokens: 4096,
      system: EXTRACTION_SYSTEM_PROMPT,
      messages: [
        {
          role: 'user',
          content: [
            {
              type: 'document',
              source: {
                type: 'base64',
                media_type: 'application/pdf',
                data: pdfBase64,
              },
            },
            {
              type: 'text',
              text: `Extract all SIRS component data from this engineering report: ${filename}. Find every component that falls under the 8 HB 913 mandated categories. Return only the JSON structure specified.`,
            },
          ],
        },
      ],
    }),
  });

  if (!response.ok) {
    const errText = await response.text();
    throw new Error(`Claude API error: ${errText}`);
  }

  const data = await response.json();
  const output = data?.content?.[0]?.text ?? '[]';
  const sanitized = sanitizeClaudeJsonOutput(output);
  const parsed = JSON.parse(sanitized);

  return REQUIRED_COMPONENTS.map((name) => {
    const match = parsed.find((row) => row.name?.toLowerCase().includes(name.toLowerCase()));
    return {
      name,
      estimatedReplacementCost: Number(match?.estimatedReplacementCost) || 0,
      remainingUsefulLifeYears: Number(match?.remainingUsefulLifeYears) || 0,
      currentReserveBalance: Number(match?.currentReserveBalance) || 0,
    };
  });
}
